import React, { useState, useEffect } from 'react';
import API from '../../services/api'; 
import { THEME } from '../../constants/theme'; 
import { toast, Toaster } from 'react-hot-toast';

const UserManagement = () => {
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]); // Default empty array
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [isPassModalOpen, setIsPassModalOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [form, setForm] = useState({
        full_name: '', username: '', password: '', 
        email: '', contact: '', salary: '', role_ids: []
    });

    // 🟢 FIXED: FetchData logic to match new backend format
    const fetchData = async () => {
        try {
            const [empRes, roleRes] = await Promise.all([
                API.get('/v1/employees/list'), // Updated path as per new routes
                API.get('/admin/roles')        // Backend response: { success: true, data: [...] }
            ]);

            // Set Employees
            if (empRes.data.success) {
                const savedUser = JSON.parse(localStorage.getItem('user'));
                const myId = savedUser?.id; 
                const filtered = empRes.data.data.filter(emp => Number(emp.id) !== Number(myId));
                setEmployees(filtered);
            }

            // 🟢 FIX: Accessing .data.data for roles array
            if (roleRes.data.success && Array.isArray(roleRes.data.data)) {
                setRoles(roleRes.data.data);
            } else {
                setRoles([]); // Fallback to avoid .map() error
            }
        } catch (err) {
            console.error("Data fetch error:", err);
            toast.error("Error loading data. Check if server is running!");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleModal = (emp = null) => {
        if (emp) {
            setIsEditMode(true);
            setCurrentId(emp.id);
            const assignedIds = emp.role_ids ? String(emp.role_ids).split(',').map(id => Number(id.trim())) : [];
            setForm({ 
                full_name: emp.full_name, 
                username: emp.username, 
                password: '', 
                email: emp.email, 
                contact: emp.contact, 
                salary: emp.salary, 
                role_ids: assignedIds 
            });
        } else {
            setIsEditMode(false);
            setForm({ full_name: '', username: '', password: '', email: '', contact: '', salary: '', role_ids: [] });
        }
        setIsModalOpen(true);
    };

    const handleRoleChange = (roleId) => {
        const idNum = Number(roleId);
        setForm(prev => {
            const updatedIds = prev.role_ids.includes(idNum) 
                ? prev.role_ids.filter(id => id !== idNum) 
                : [...prev.role_ids, idNum];
            return { ...prev, role_ids: updatedIds };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditMode) {
                await API.put(`/v1/employees/${currentId}`, form);
                toast.success("Employee Record Updated!");
            } else {
                await API.post('/v1/employees/create', form);
                toast.success("New Employee Created!");
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) { 
            toast.error(err.response?.data?.message || "Operation failed!"); 
        }
    };

    const deleteEmployee = async (id) => {
        if (window.confirm("Are you sure? This will remove access permanently.")) {
            try {
                await API.delete(`/v1/employees/${id}`);
                fetchData();
                toast.success("Employee Deleted");
            } catch (err) { toast.error("Delete failed"); }
        }
    };

    const handlePasswordReset = async () => {
        if (!newPassword) return toast.error("Enter a password");
        try {
            await API.patch(`/v1/employees/${currentId}/change-password`, { newPassword });
            toast.success("Password Updated Successfully");
            setIsPassModalOpen(false);
            setNewPassword('');
        } catch (err) {
            toast.error("Failed to reset password");
        }
    };

    return (
        <div style={containerStyle}>
            <Toaster position="top-right" />
            <div style={headerStyle}>
                <div>
                    <h1 style={titleStyle}>Employee Master</h1>
                    <p style={{color: THEME.textMuted, margin: 0, fontSize: '14px'}}>Manage staff access and permissions</p>
                </div>
                <button onClick={() => toggleModal()} style={addBtn}>+ New Employee</button>
            </div>

            <div style={tableCard}>
                <table style={tableStyle}>
                    <thead style={theadStyle}>
                        <tr>
                            <th style={thStyle}>Employee Details</th>
                            <th style={thStyle}>Contact & Email</th>
                            <th style={thStyle}>Designation / Roles</th>
                            <th style={thStyle}>Salary (PKR)</th>
                            <th style={thStyle}>Management</th>
                        </tr>
                    </thead>
                    <tbody>
                        {employees.map(emp => (
                            <tr key={emp.id} style={trStyle}>
                                <td style={tdStyle}>
                                    <div style={{fontWeight: '700', color: THEME.textMain}}>{emp.full_name}</div>
                                    <div style={{fontSize: '12px', color: THEME.textMuted}}>@{emp.username}</div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={{fontSize: '13px'}}>{emp.email}</div>
                                    <div style={{fontSize: '12px', color: THEME.secondary}}>{emp.contact}</div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={badgeContainer}>
                                        {emp.roles?.split(',').map(r => <span key={r} style={roleBadge}>{r}</span>)}
                                    </div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={salaryBox}>
                                        <span style={{fontSize: '11px', opacity: 0.8}}>Rs.</span> {Number(emp.salary || 0).toLocaleString()}
                                    </div>
                                </td>
                                <td style={tdStyle}>
                                    <button onClick={() => toggleModal(emp)} style={editLink}>Edit</button>
                                    <button onClick={() => { setCurrentId(emp.id); setIsPassModalOpen(true); }} style={passLink}>Reset Pass</button>
                                    <button onClick={() => deleteEmployee(emp.id)} style={deleteLink}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- PASSWORD RESET MODAL --- */}
            {isPassModalOpen && (
                <div style={modalOverlay}>
                    <div style={{...modalContent, width: '400px'}}>
                        <div style={modalHeader}>
                            <h2 style={{margin:0, fontSize:'18px'}}>Reset Password</h2>
                            <button onClick={() => setIsPassModalOpen(false)} style={closeBtn}>&times;</button>
                        </div>
                        <div style={{padding: '10px 0'}}>
                             <label style={lab}>New Secure Password</label>
                             <input type="password" placeholder="••••••••" style={inp} onChange={(e) => setNewPassword(e.target.value)} />
                        </div>
                        <button onClick={handlePasswordReset} style={{...submitBtn, backgroundColor: THEME.warning}}>Update Password</button>
                    </div>
                </div>
            )}

            {/* --- ADD/EDIT MODAL --- */}
            {isModalOpen && (
                <div style={modalOverlay}>
                    <div style={modalContent}>
                        <div style={modalHeader}>
                            <h2 style={{margin: 0, fontSize: '20px', color: THEME.textMain}}>{isEditMode ? 'Update Employee' : 'Register New Employee'}</h2>
                            <button onClick={() => setIsModalOpen(false)} style={closeBtn}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={formGrid}>
                                <div style={field}><label style={lab}>Full Name</label><input value={form.full_name} style={inp} required onChange={e => setForm({...form, full_name: e.target.value})} /></div>
                                <div style={field}><label style={lab}>Username</label><input value={form.username} style={inp} required onChange={e => setForm({...form, username: e.target.value})} /></div>
                                <div style={field}><label style={lab}>Email</label><input type="email" value={form.email} style={inp} required onChange={e => setForm({...form, email: e.target.value})} /></div>
                                <div style={field}><label style={lab}>Contact</label><input value={form.contact} style={inp} required onChange={e => setForm({...form, contact: e.target.value})} /></div>
                                {!isEditMode && <div style={field}><label style={lab}>Password</label><input type="password" style={inp} required onChange={e => setForm({...form, password: e.target.value})} /></div>}
                                <div style={field}><label style={lab}>Salary</label><input type="number" value={form.salary} style={inp} required onChange={e => setForm({...form, salary: e.target.value})} /></div>
                            </div>
                            <div style={roleSection}>
                                <div style={{...lab, marginBottom: '10px'}}>Assign Access Permissions (Roles)</div>
                                <div style={roleScrollGrid}>
                                    {/* 🟢 FIXED: Safety check roles is array before mapping */}
                                    {Array.isArray(roles) && roles.map(r => (
                                        <label key={r.id} style={checkboxLabel}>
                                            <input type="checkbox" checked={form.role_ids.includes(Number(r.id))} onChange={() => handleRoleChange(r.id)} /> 
                                            <span style={{marginLeft: '8px'}}>{r.role_name.replace('_', ' ')}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <button type="submit" style={{...submitBtn, backgroundColor: THEME.success}}>{isEditMode ? 'Save Changes' : 'Create Account'}</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STYLES (Keep existing ones) ---
const containerStyle = { padding: '20px 40px', background: THEME.bodyBg, minHeight: '100vh' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' };
const titleStyle = { margin: 0, color: THEME.textMain, fontSize: '24px', fontWeight: '800' };
const addBtn = { background: THEME.secondary, color: THEME.white, border: 'none', padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' };
const tableCard = { background: THEME.white, borderRadius: '16px', border: `1px solid ${THEME.border}`, overflow: 'hidden' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const theadStyle = { background: '#f8fafc', borderBottom: `1px solid ${THEME.border}` };
const thStyle = { padding: '16px', textAlign: 'left', fontSize: '11px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '15px 16px', borderBottom: `1px solid ${THEME.border}`, verticalAlign: 'middle' };
const trStyle = { transition: '0.2s' };
const roleBadge = { background: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', border: `1px solid #e2e8f0`, marginRight: '4px', marginBottom: '4px' };
const badgeContainer = { display: 'flex', gap: '2px', flexWrap: 'wrap', maxWidth: '200px' };
const salaryBox = { fontWeight: '800', color: '#059669', background: '#ecfdf5', padding: '6px 10px', borderRadius: '8px', fontSize: '13px', border: '1px solid #10b98133', display: 'inline-block', whiteSpace: 'nowrap' };
const editLink = { color: THEME.secondary, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', marginRight: '15px', fontSize: '13px' };
const deleteLink = { color: THEME.danger, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' };
const passLink = { color: THEME.warning, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', marginRight: '16px', whiteSpace: 'nowrap', fontSize: '13px' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' };
const modalContent = { background: THEME.white, padding: '30px', borderRadius: '24px', width: '600px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10, paddingBottom: '10px' };
const closeBtn = { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' };
const formGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '15px' };
const field = { display: 'flex', flexDirection: 'column', gap: '5px' };
const lab = { fontSize: '11px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase' };
const inp = { padding: '12px', borderRadius: '10px', border: `1px solid ${THEME.border}`, width: '100%', boxSizing: 'border-box', fontSize: '14px' };
const roleSection = { marginTop: '20px', background: '#f8fafc', padding: '15px', borderRadius: '15px', border: `1px solid ${THEME.border}` };
const roleScrollGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const checkboxLabel = { display: 'flex', alignItems: 'center', background: THEME.white, padding: '10px', borderRadius: '8px', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontSize: '13px' };
const submitBtn = { width: '100%', marginTop: '25px', padding: '15px', color: THEME.white, border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' };

export default UserManagement;